import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/*
 * PERFORMANCE OPTIMIZATION
 *
 * Instead of every slideshow component registering its own
 * document-level visibilitychange listener, keep track of all
 * active slideshows and use one shared listener.
 *
 * This is especially important on collection pages where dozens
 * of product cards can exist after infinite scrolling.
 */
const activeSlideshows = new Set();

const handleDocumentVisibilityChange = () => {
  for (const slideshow of activeSlideshows) {
    if (!slideshow.isConnected) {
      activeSlideshows.delete(slideshow);
      continue;
    }

    if (document.hidden) {
      slideshow.pause();
    } else {
      slideshow.resume();
    }
  }
};

document.addEventListener(
  'visibilitychange',
  handleDocumentVisibilityChange
);

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) {
          return;
        }

        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');

        if (slide_id) {
          this.select(
            { id: slide_id },
            undefined,
            { animate: false }
          );
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    /*
     * Wait for any in-progress view transitions to finish.
     */
    if (viewTransition.current) {
      await viewTransition.current;

      /*
       * The slideshow may have been disconnected while the transition
       * was running.
       */
      if (!this.isConnected) {
        return;
      }
    }

    const { scroller } = this.refs;

    /*
     * Create the scroller for this slideshow.
     */
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener(
      'mousedown',
      this.#handleMouseDown
    );

    this.addEventListener(
      'mouseenter',
      this.suspend
    );

    this.addEventListener(
      'mouseleave',
      this.resume
    );

    this.addEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    /*
     * PERFORMANCE OPTIMIZATION:
     *
     * Register this slideshow in the shared collection instead of
     * adding another document-level visibilitychange listener.
     */
    activeSlideshows.add(this);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    /*
     * Batch reads and writes to the DOM.
     */
    scheduler.schedule(() => {
      /*
       * The component may have been removed before the scheduled
       * callback executes.
       */
      if (!this.isConnected || !this.#scroll) {
        return;
      }

      let visibleSlidesAmount = 0;

      const initialSlideId =
        this.initialSlide?.getAttribute('slide-id');

      if (
        this.initialSlideIndex !== 0 &&
        initialSlideId
      ) {
        this.select(
          { id: initialSlideId },
          undefined,
          { animate: false }
        );

        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount =
          this.#updateVisibleSlides();

        if (visibleSlidesAmount === 0) {
          this.select(
            0,
            undefined,
            { animate: false }
          );

          visibleSlidesAmount = 1;
        }
      }

      /*
       * Observe size changes because the amount of visible slides
       * can change when the card or viewport changes size.
       */
      this.#resizeObserver = new ResizeObserver(
        async () => {
          if (!this.isConnected) {
            return;
          }

          if (viewTransition.current) {
            await viewTransition.current;

            if (!this.isConnected) {
              return;
            }
          }

          if (visibleSlidesAmount > 1) {
            this.#updateVisibleSlides();
          }

          if (this.hasAttribute('auto-hide-controls')) {
            this.#updateControlsVisibility();
          }
        }
      );

      this.#resizeObserver.observe(
        this.refs.slideshowContainer
      );
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { scroller } = this.refs;

    scroller.removeEventListener(
      'mousedown',
      this.#handleMouseDown
    );

    this.removeEventListener(
      'mouseenter',
      this.suspend
    );

    this.removeEventListener(
      'mouseleave',
      this.resume
    );

    this.removeEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    /*
     * Remove from the shared slideshow collection.
     */
    activeSlideshows.delete(this);

    /*
     * Destroy the scroller and observer when the component
     * is removed from the DOM.
     */
    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = undefined;
    }

    /*
     * Stop autoplay timers immediately.
     */
    this.suspend();
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return (
      this.parentElement?.closest(
        'slideshow-component'
      ) !== null
    );
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   *
   * @param {number|string|{id: string}} input
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  async select(input, event, options = {}) {
    if (
      this.#disabled ||
      !this.refs.slides?.length
    ) {
      return;
    }

    /*
     * Keep aria-hidden and inert synchronized.
     */
    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
      }

      slide.setAttribute(
        'aria-hidden',
        'true'
      );

      slide.setAttribute(
        'inert',
        ''
      );
    }

    /*
     * Figure out the raw desired index.
     */
    let requestedIndex = (() => {
      if (typeof input === 'number') {
        return input;
      }

      if (typeof input === 'string') {
        return parseInt(input, 10);
      }

      if ('id' in input) {
        const requestedSlide =
          this.refs.slides.find(
            (slide) =>
              slide.getAttribute('slide-id') ==
              input.id
          );

        if (
          !requestedSlide ||
          !this.slides
        ) {
          return;
        }

        /*
         * Force the slide to be revealed if it is hidden.
         */
        if (
          requestedSlide.hasAttribute('hidden')
        ) {
          requestedSlide.setAttribute(
            'reveal',
            ''
          );

          requestedSlide.removeAttribute(
            'inert'
          );
        }

        return this.slides.indexOf(
          requestedSlide
        );
      }
    })();

    const { current } = this;

    /*
     * Guard invalid selections.
     */
    if (
      requestedIndex === undefined ||
      isNaN(requestedIndex) ||
      requestedIndex === current
    ) {
      return;
    }

    const { slides } = this;

    if (!slides?.length) {
      return;
    }

    if (!this.infinite) {
      requestedIndex = clamp(
        requestedIndex,
        0,
        slides.length - 1
      );
    }

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    /*
     * Decide the actual target index.
     */
    let index = requestedIndex;

    if (requestedIndex < 0) {
      index = lastIndex;
    } else if (requestedIndex > lastIndex) {
      index = 0;
    }

    const isAdjacentSlide =
      Math.abs(index - current) <= 1 &&
      requestedIndex >= 0 &&
      requestedIndex <= lastIndex;

    const { visibleSlides } = this;

    const instant =
      prefersReducedMotion() || !animate;

    /*
     * If the jump is more than one slide, use the
     * placeholder/reorder technique.
     */
    if (
      !instant &&
      !isAdjacentSlide &&
      visibleSlides.length === 1
    ) {
      this.#disabled = true;

      await this.#scroll.finished;

      const targetSlide = slides[index];
      const currentSlide = slides[current];

      if (
        !targetSlide ||
        !currentSlide
      ) {
        this.#disabled = false;
        return;
      }

      const placeholder =
        document.createElement(
          'slideshow-slide'
        );

      targetSlide.before(placeholder);

      if (requestedIndex < current) {
        currentSlide.before(
          targetSlide
        );
      } else {
        currentSlide.after(
          targetSlide
        );
      }

      if (current === 0) {
        this.#scroll.to(
          currentSlide,
          { instant: true }
        );
      }

      queueMicrotask(async () => {
        await this.#scroll.finished;

        this.#disabled = false;

        placeholder.replaceWith(
          targetSlide
        );

        this.#scroll.to(
          targetSlide,
          { instant: true }
        );
      });
    }

    const slide = slides[index];

    if (!slide) {
      this.#disabled = false;
      return;
    }

    const previousIndex = this.current;

    /*
     * The selected slide is visible and must be available
     * to keyboard users.
     */
    slide.setAttribute(
      'aria-hidden',
      'false'
    );

    slide.removeAttribute('inert');

    this.#scroll.to(
      slide,
      { instant }
    );

    this.#centerSelectedThumbnail(
      index,
      instant ? 'instant' : 'smooth'
    );

    /*
     * Keep the internal current index synchronized.
     */
    this.current = index;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute(
          'slide-id'
        ),
      })
    );
  }

  /**
   * Advances to the next slide.
   */
  next(event, options) {
    event?.preventDefault();

    this.select(
      this.nextIndex,
      event,
      options
    );
  }

  /**
   * Goes back to the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();

    this.select(
      this.previousIndex,
      event,
      options
    );
  }

  /**
   * Starts automatic slide playback.
   *
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) {
      return;
    }

    this.paused = false;

    this.#interval = setInterval(() => {
      if (
        this.matches(':hover') ||
        document.hidden
      ) {
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute(
        'paused',
        ''
      );
    } else {
      this.removeAttribute(
        'paused'
      );
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (
      !this.autoplay ||
      this.paused
    ) {
      return;
    }

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(
      this.autoplayInterval
    );
  }

  get autoplayInterval() {
    const interval =
      this.getAttribute('autoplay');

    const value = parseInt(
      `${interval}`,
      10
    );

    if (Number.isNaN(value)) {
      return undefined;
    }

    return value * 1000;
  }

  /**
   * The current slide index.
   *
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  set current(value) {
    const {
      current,
      thumbnails,
      dots,
      slides,
      previous,
      next,
    } = this.refs;

    this.#current = value;

    if (current) {
      current.textContent =
        `${value + 1}`;
    }

    for (const controls of [
      thumbnails,
      dots,
    ]) {
      controls?.forEach((el, i) =>
        el.setAttribute(
          'aria-selected',
          `${i === value}`
        )
      );
    }

    if (previous) {
      previous.disabled = Boolean(
        !this.infinite &&
        value === 0
      );
    }

    if (next) {
      next.disabled = Boolean(
        !this.infinite &&
        slides &&
        this.nextIndex >= slides.length
      );
    }
  }

  get infinite() {
    return (
      this.getAttribute('infinite') != null
    );
  }

  get visibleSlides() {
    return getVisibleElements(
      this.refs.scroller,
      this.slides,
      SLIDE_VISIBLITY_THRESHOLD,
      'x'
    );
  }

  get previousIndex() {
    const {
      current,
      visibleSlides,
    } = this;

    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return current - modifier;
  }

  get nextIndex() {
    const {
      current,
      visibleSlides,
    } = this;

    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return current + modifier;
  }

  get atStart() {
    const {
      current,
      slides,
    } = this;

    return slides?.length
      ? current === 0
      : false;
  }

  get atEnd() {
    const {
      current,
      slides,
    } = this;

    return slides?.length
      ? current === slides.length - 1
      : false;
  }

  /**
   * Sets the disabled attribute.
   */
  set disabled(value) {
    this.setAttribute(
      'disabled',
      String(value)
    );
  }

  /**
   * Whether the slideshow is disabled.
   */
  get disabled() {
    return (
      this.getAttribute('disabled') ===
        'true' ||
      (
        this.hasAttribute(
          'mobile-disabled'
        ) &&
        !mediaQueryLarge.matches
      )
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled.
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes.
   */
  #resizeObserver;

  /**
   * Callback invoked on user initiated scroll to sync
   * the current slide index and emit a slide change event.
   */
  #handleScroll = () => {
    const previousIndex =
      this.#current;

    const index = this.#sync();

    if (
      index === previousIndex
    ) {
      return;
    }

    const slide =
      this.slides?.[index];

    if (!slide) {
      return;
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute(
          'slide-id'
        ),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute(
      'transitioning',
      ''
    );
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();

    this.removeAttribute(
      'transitioning'
    );
  };

  /**
   * Synchronizes the scroll position and updates
   * the current slide index.
   */
  #sync = () => {
    const { slides } = this;

    if (!slides) {
      return (this.current = 0);
    }

    const visibleSlides =
      this.visibleSlides;

    if (!visibleSlides.length) {
      return this.current;
    }

    const { axis } = this.#scroll;
    const { scroller } = this.refs;

    const centers =
      visibleSlides.map(
        (slide) =>
          center(slide, axis)
      );

    const referencePoint =
      visibleSlides.length > 1
        ? scroller.getBoundingClientRect()[
            axis
          ]
        : center(
            scroller,
            axis
          );

    const closestCenter =
      closest(
        centers,
        referencePoint
      );

    const closestVisibleSlide =
      visibleSlides[
        centers.indexOf(
          closestCenter
        )
      ];

    if (!closestVisibleSlide) {
      return (this.current = 0);
    }

    const index =
      slides.indexOf(
        closestVisibleSlide
      );

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (
      !slides ||
      slides.length <= 1
    ) {
      return;
    }

    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    if (
      this.disabled ||
      this.#dragging
    ) {
      return;
    }

    /*
     * Do not capture drag events from 3D model elements.
     */
    if (
      event.target.closest(
        'model-viewer'
      )
    ) {
      return;
    }

    event.preventDefault();

    const { axis } =
      this.#scroll;

    const startPosition =
      event[axis];

    const controller =
      new AbortController();

    const { signal } =
      controller;

    const startTime =
      performance.now();

    let previous =
      startPosition;

    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles pointer movement.
     */
    const onPointerMove = (
      event
    ) => {
      const current =
        event[axis];

      const initialDelta =
        startPosition -
        current;

      if (!initialDelta) {
        return;
      }

      if (!moved) {
        moved = true;

        this.setPointerCapture(
          event.pointerId
        );

        document.addEventListener(
          'click',
          preventDefault,
          {
            once: true,
            signal,
          }
        );

        const movingRight =
          initialDelta < 0;

        const movingLeft =
          initialDelta > 0;

        const closestSlideshow =
          this.parentElement?.closest(
            'slideshow-component'
          );

        const isNested =
          closestSlideshow instanceof
            Slideshow &&
          closestSlideshow !== this;

        const cannotMoveInDirection =
          (
            movingRight &&
            this.atStart
          ) ||
          (
            movingLeft &&
            this.atEnd
          );

        if (
          isNested &&
          cannotMoveInDirection
        ) {
          controller.abort();
          this.#dragging = false;
          return;
        }

        this.pause();

        this.setAttribute(
          'dragging',
          ''
        );
      }

      event.stopImmediatePropagation();

      const delta =
        previous - current;

      const timeDelta =
        performance.now() -
        startTime;

      velocity = Math.round(
        (delta / timeDelta) *
          1000
      );

      previous = current;

      distanceTravelled +=
        Math.abs(delta);

      this.#scroll.by(
        delta,
        { instant: true }
      );
    };

    /**
     * Handles pointer release.
     */
    const onPointerUp = async (
      event
    ) => {
      controller.abort();

      const {
        current,
        slides,
      } = this;

      const { scroller } =
        this.refs;

      this.#dragging = false;

      if (
        !slides?.length ||
        !scroller
      ) {
        return;
      }

      const direction =
        Math.sign(velocity);

      const next =
        this.#sync();

      const modifier =
        current !== next ||
        Math.abs(velocity) < 10 ||
        distanceTravelled < 10
          ? 0
          : direction;

      const newIndex =
        clamp(
          next + modifier,
          0,
          slides.length - 1
        );

      const newSlide =
        slides[newIndex];

      const currentIndex =
        this.current;

      if (!newSlide) {
        throw new Error(
          `Slide not found at index ${newIndex}`
        );
      }

      this.#scroll.to(
        newSlide
      );

      this.removeAttribute(
        'dragging'
      );

      this.releasePointerCapture(
        event.pointerId
      );

      this.#centerSelectedThumbnail(
        newIndex
      );

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute(
            'slide-id'
          ),
        })
      );

      this.current =
        newIndex;

      await this.#scroll.finished;

      if (this.#dragging) {
        return;
      }

      this.#scroll.snap = true;

      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener(
      'pointermove',
      onPointerMove,
      { signal }
    );

    document.addEventListener(
      'pointerup',
      onPointerUp,
      { signal }
    );

    /*
     * pointerDown calls onPointerUp to fix an issue where the first
     * tap-and-drag on the zoom dialog is captured unexpectedly.
     */
    document.addEventListener(
      'pointerdown',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercancel',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercapturelost',
      onPointerUp,
      { signal }
    );
  };

  #handlePointerEnter = () => {
    this.setAttribute(
      'actioned',
      ''
    );
  };

  get slides() {
    return this.refs.slides?.filter(
      (slide) =>
        !slide.hasAttribute('hidden') ||
        slide.hasAttribute('reveal')
    );
  }

  /**
   * The initial slide index.
   */
  get initialSlideIndex() {
    const initialSlide =
      this.getAttribute(
        'initial-slide'
      );

    if (
      initialSlide == null
    ) {
      return 0;
    }

    return parseInt(
      initialSlide,
      10
    );
  }

  /**
   * Updates visibility when the page becomes hidden/visible.
   *
   * Kept as a method because the shared document-level listener
   * calls pause/resume directly.
   */
  #handleVisibilityChange = () =>
    document.hidden
      ? this.pause()
      : this.resume();

  #updateControlsVisibility() {
    if (
      !this.hasAttribute(
        'auto-hide-controls'
      )
    ) {
      return;
    }

    const {
      scroller,
      slideshowControls,
    } = this.refs;

    if (
      !(
        slideshowControls instanceof
        HTMLElement
      )
    ) {
      return;
    }

    slideshowControls.hidden =
      scroller.scrollWidth <=
      scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail.
   */
  #centerSelectedThumbnail(
    index,
    behavior = 'smooth'
  ) {
    const selectedThumbnail =
      this.refs.thumbnails?.[
        index
      ];

    if (!selectedThumbnail) {
      return;
    }

    const {
      thumbnailsContainer,
    } = this.refs;

    if (
      !thumbnailsContainer ||
      !(
        thumbnailsContainer instanceof
        HTMLElement
      )
    ) {
      return;
    }

    const {
      slideshowControls,
    } = this.refs;

    if (
      !slideshowControls ||
      !(
        slideshowControls instanceof
        HTMLElement
      )
    ) {
      return;
    }

    scrollIntoView(
      selectedThumbnail,
      {
        ancestor:
          thumbnailsContainer,
        behavior,
        block: 'center',
        inline: 'center',
      }
    );
  }

  #updateVisibleSlides() {
    const { slides } = this;

    if (
      !slides ||
      !slides.length
    ) {
      return 0;
    }

    const visibleSlides =
      this.visibleSlides;

    scheduler.schedule(() => {
      /*
       * The slideshow may have been removed before the scheduled
       * DOM update executes.
       */
      if (!this.isConnected) {
        return;
      }

      slides.forEach(
        (slide) => {
          const isVisible =
            visibleSlides.includes(
              slide
            );

          slide.setAttribute(
            'aria-hidden',
            `${!isVisible}`
          );

          if (isVisible) {
            slide.removeAttribute(
              'inert'
            );
          } else {
            slide.setAttribute(
              'inert',
              ''
            );
          }
        }
      );
    });

    return visibleSlides.length;
  }
}

if (
  !customElements.get(
    'slideshow-component'
  )
) {
  customElements.define(
    'slideshow-component',
    Slideshow
  );
}